import { Component, Inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    TranslatePipe,
    LoaderComponent,
    RouterLink,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class HomeComponent {
  email: string = '';
  password: string = '';
  errorMessage: string = '';
  isLanguageDropdownOpen = false;
  isUserDropdownOpen = false;
  isSupportViewOpen = false;
  isLoading = false;
  showPassword = false;
  rememberMe = false;
  activeDropdown: string | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private translate: TranslateService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.translate.setFallbackLang('en');
    const savedLang = this.getSavedLanguage();
    this.translate.use(savedLang);
    // Restore "remember me" state and pre-fill email if requested
    try {
      if (isPlatformBrowser(this.platformId)) {
        const savedRemember = localStorage.getItem('rememberMe') === 'true';
        if (savedRemember) {
          this.rememberMe = true;
          const savedEmail = localStorage.getItem('rememberedEmail');
          if (savedEmail) {
            this.email = savedEmail;
          }
        }
      }
    } catch (e) {
      console.warn('Erreur lors de la lecture de localStorage:', e);
    }
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const clickedInsideLanguage = target.closest('.language-dropdown');
    const clickedInsideUser = target.closest('.user-dropdown');

    if (!clickedInsideLanguage) {
      this.isLanguageDropdownOpen = false;
    }
    if (!clickedInsideUser) {
      this.isUserDropdownOpen = false;
      this.isSupportViewOpen = false;
    }
  }

  selectLanguage(lang: string) {
    this.changeLanguage(lang);
    this.isLanguageDropdownOpen = false;
  }

  changeLanguage(lang: string) {
    this.translate.use(lang);
    this.saveLanguage(lang);
  }

  getCurrentLanguage(): string {
    return this.translate.currentLang || 'fr';
  }

  getCurrentLanguageFlag(): string {
    const lang = this.getCurrentLanguage();
    switch (lang) {
      case 'fr':
        return 'https://flagcdn.com/w20/fr.png';
      case 'ar':
        return 'https://flagcdn.com/w20/tn.png';
      default:
        return 'https://flagcdn.com/w20/gb.png';
    }
  }

  getHeroImage(): string {
    const lang = this.getCurrentLanguage();
    switch (lang) {
      case 'ar':
        return '../../../assets/icons/arabic.png';
      case 'en':
        return '../../../assets/icons/eng.png';
      case 'fr':
        return '../../../assets/icons/fr.png';
      default:
        return '../../../assets/icons/fr.png';
    }
  }

  private saveLanguage(lang: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('selectedLanguage', lang);
    }
  }

  private getSavedLanguage(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('selectedLanguage') || 'fr';
    }
    return 'fr';
  }

  private getCleanErrorMessage(...messages: Array<unknown>): string | null {
    for (const message of messages) {
      if (typeof message !== 'string') {
        continue;
      }

      const cleaned = message.trim();
      if (!cleaned) {
        continue;
      }

      const lowered = cleaned.toLowerCase();
      if (lowered === 'null' || lowered === 'undefined') {
        continue;
      }

      return cleaned;
    }

    return null;
  }

  handleSubmit(): void {
    if (!this.email?.trim() || !this.password) {
      this.errorMessage = this.translate.instant(
        'homepage.loginForm.missingCredentials',
      );
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const apiBaseUrl = environment.apiUrl.replace(/\/$/, '');
    const url = `${apiBaseUrl}/user/UserByLoginAndPassword`;
    const body = { login: this.email.trim(), password: this.password };

    this.http
      .post<any>(url, body, { headers: { 'Content-Type': 'application/json' } })
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          if (res?.response?.iduser && isPlatformBrowser(this.platformId)) {
            // Check if user role is 4
            const userRole = res?.response?.role;
            if (userRole !== 4) {
              this.errorMessage = this.translate.instant(
                'homepage.loginForm.unauthorized',
              ) || 'Access denied. Only parents are authorized.';
              return;
            }

            // Use AuthService to handle login
            try {
              this.authService.login(res.response);

              localStorage.setItem('showVideoModal', 'true');
              // Persist remember-me choice and email if requested
              try {
                if (isPlatformBrowser(this.platformId)) {
                  if (this.rememberMe) {
                    localStorage.setItem('rememberMe', 'true');
                    localStorage.setItem('rememberedEmail', this.email.trim());
                  } else {
                    localStorage.removeItem('rememberMe');
                    localStorage.removeItem('rememberedEmail');
                  }
                }
              } catch (e) {
                console.warn('Erreur lors de la sauvegarde remember-me:', e);
              }
            } catch (e) {
              console.warn('Erreur lors de la sauvegarde localStorage:', e);
            }

            // If a returnUrl was provided (guard or other source), go there; otherwise default to /accueil
            const returnUrl =
              this.router.parseUrl(this.router.url).queryParams['returnUrl'] ||
              undefined;
            if (returnUrl) {
              try {
                // Remove any pendingCreds stored earlier
                if (isPlatformBrowser(this.platformId)) {
                  sessionStorage.removeItem('pendingCreds');
                }

                // Clean credentials from the URL if any
                if (isPlatformBrowser(this.platformId)) {
                  const url = new URL(window.location.href);
                  url.searchParams.delete('email');
                  url.searchParams.delete('password');
                  history.replaceState({}, '', url.pathname + url.search);
                }
              } catch (e) {
                // ignore
              }

              this.router.navigateByUrl(returnUrl);
            } else {
              // Remove any pending creds on normal navigation too
              try {
                if (isPlatformBrowser(this.platformId)) {
                  sessionStorage.removeItem('pendingCreds');
                }
              } catch (e) { }
              this.router.navigate(['/accueil']);
            }
          } else {
            this.errorMessage =
              this.getCleanErrorMessage(res?.response?.error, res?.error) ||
              this.translate.instant('homepage.loginForm.invalidCredentials');
          }
        },
        error: (err) => {
          this.isLoading = false;
          console.error('HTTP Error:', err);

          if (err.status === 500) {
            this.errorMessage = this.translate.instant(
              'common.errors.serverError',
            );
          } else if (err.status === 0) {
            this.errorMessage = this.translate.instant(
              'common.errors.networkError',
            );
          } else {
            this.errorMessage = this.translate.instant(
              'homepage.loginForm.errorOccurred',
            );
          }
        },
      });
  }

  toggleLanguageDropdown() {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;

    if (this.isUserDropdownOpen) {
      this.isUserDropdownOpen = false;
    }
  }

  toggleUserDropdown() {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;

    if (this.isLanguageDropdownOpen) {
      this.isLanguageDropdownOpen = false;
    }
  }

  toggleDropdown(dropdown: string) {
    if (this.activeDropdown === dropdown) {
      this.activeDropdown = null;
    } else {
      this.activeDropdown = dropdown;
    }
  }

  openExternalLink(url: string) {
    window.open(url, '_blank', 'noopener');
  }
}
